/**
 * Every word the setup wizard shows, in English and Arabic. Kept in one table
 * so the two languages are reviewed side by side and neither can be missing a
 * line (the type makes `ar` mandatory).
 */
export interface Bi {
  en: string;
  ar: string;
}

const s = (en: string, ar: string): Bi => ({ en, ar });

export const T = {
  brandTag: s('Business setup', 'إعداد النشاط التجاري'),
  website: s('sismatix.com', 'sismatix.com'),
  needHelp: s('Need help? Contact us', 'تحتاج مساعدة؟ تواصل معنا'),
  stepOf: s('Step {n} of {total}', 'الخطوة {n} من {total}'),
  back: s('Back', 'رجوع'),
  next: s('Continue', 'متابعة'),
  saving: s('Saving…', 'جارٍ الحفظ…'),
  exit: s('Sign out', 'تسجيل الخروج'),
  edit: s('Edit', 'تعديل'),
  optional: s('Optional', 'اختياري'),
  required: s('Required', 'مطلوب'),
  loading: s('Preparing your setup…', 'جارٍ تجهيز الإعداد…'),
  retry: s('Try again', 'إعادة المحاولة'),

  steps: {
    welcome: s('Welcome', 'مرحباً'),
    type: s('Business type', 'نوع النشاط'),
    info: s('Business details', 'بيانات النشاط'),
    branch: s('Branch', 'الفرع'),
    features: s('Features', 'المزايا'),
    review: s('Review', 'المراجعة'),
    done: s('Done', 'تم'),
  },

  welcome: {
    hi: s('Hi {name}, welcome to Sismatix', 'أهلاً {name}، مرحباً بك في سيسماتكس'),
    lead: s("Let's get your business ready.", 'لنجهّز نشاطك التجاري.'),
    body: s(
      'This quick setup configures your system around the way your business works. It takes about three minutes, and everything can be changed later in Settings.',
      'يقوم هذا الإعداد السريع بتهيئة النظام حسب طبيعة عملك. يستغرق نحو ثلاث دقائق، ويمكن تعديل كل شيء لاحقاً من الإعدادات.',
    ),
    points: [
      s('Choose your business type', 'اختر نوع نشاطك'),
      s('Add your logo, country and currency', 'أضف شعارك ودولتك وعملتك'),
      s('Set up your first branch', 'جهّز فرعك الأول'),
      s('Switch on the features you need', 'فعّل المزايا التي تحتاجها'),
    ],
    start: s("Let's get started", 'لنبدأ'),
  },

  type: {
    title: s('What type of business do you operate?', 'ما نوع النشاط الذي تديره؟'),
    lead: s(
      'Your choice shapes the menus, settings and workflows you will see.',
      'يحدد اختيارك القوائم والإعدادات وسير العمل التي ستظهر لك.',
    ),
    restaurant: s('Restaurant / Café', 'مطعم / مقهى'),
    restaurantDesc: s('Manage tables, orders, kitchen and delivery.', 'إدارة الطاولات والطلبات والمطبخ والتوصيل.'),
    retail: s('Retail / Store', 'متجر تجزئة'),
    retailDesc: s(
      'Manage products, inventory, sales and customers.',
      'إدارة المنتجات والمخزون والمبيعات والعملاء.',
    ),
    salon: s('Salon / Beauty', 'صالون / تجميل'),
    salonDesc: s(
      'Manage services, appointments, staff and customers.',
      'إدارة الخدمات والمواعيد والموظفين والعملاء.',
    ),
    laundry: s('Laundry / Dry cleaning', 'مغسلة / تنظيف جاف'),
    laundryDesc: s(
      'Track orders from drop-off to ready, pickup and delivery, with customer notifications.',
      'تتبّع الطلبات من الاستلام حتى الجاهزية والتسليم والتوصيل، مع إشعارات العملاء.',
    ),
    pickOne: s('Please choose your business type to continue.', 'يرجى اختيار نوع النشاط للمتابعة.'),
  },

  info: {
    title: s('Tell us about your business', 'عرّفنا بنشاطك التجاري'),
    lead: s(
      'These details appear on your receipts, invoices and online menu.',
      'تظهر هذه البيانات على الإيصالات والفواتير والقائمة الإلكترونية.',
    ),
    name: s('Business name', 'اسم النشاط'),
    nameHint: s('Your registered account name.', 'اسم حسابك المسجّل.'),
    nameAr: s('Business name in Arabic', 'اسم النشاط بالعربية'),
    logo: s('Logo', 'الشعار'),
    logoHint: s(
      'Printed on your receipts and shown on your online menu. PNG or JPG.',
      'يُطبع على الإيصالات ويظهر في القائمة الإلكترونية. PNG أو JPG.',
    ),
    upload: s('Upload logo', 'رفع الشعار'),
    replace: s('Replace', 'استبدال'),
    remove: s('Remove', 'إزالة'),
    logoError: s('That image could not be read. Try a PNG or JPG file.', 'تعذّرت قراءة الصورة. جرّب ملف PNG أو JPG.'),
    country: s('Country', 'الدولة'),
    currency: s('Currency', 'العملة'),
    currencyHint: s('All prices and reports use this currency.', 'تُستخدم هذه العملة في جميع الأسعار والتقارير.'),
    contact: s('Contact information', 'معلومات التواصل'),
    phone: s('Phone number', 'رقم الهاتف'),
    email: s('Email', 'البريد الإلكتروني'),
    address: s('Address', 'العنوان'),
    addressAr: s('Address in Arabic', 'العنوان بالعربية'),
    vat: s('Tax / VAT', 'الضريبة / ضريبة القيمة المضافة'),
    vatToggle: s('My business is registered for VAT', 'نشاطي مسجّل في ضريبة القيمة المضافة'),
    vatRate: s('VAT rate (%)', 'نسبة الضريبة (٪)'),
    vatIncluded: s('My prices already include VAT', 'أسعاري تشمل الضريبة'),
    vatNumber: s('VAT registration number', 'الرقم الضريبي'),
    crNumber: s('Commercial registration number', 'رقم السجل التجاري'),
    errCountry: s('Please choose your country.', 'يرجى اختيار الدولة.'),
    errCurrency: s('Please choose your currency.', 'يرجى اختيار العملة.'),
    errEmail: s('That email address does not look right.', 'البريد الإلكتروني غير صحيح.'),
    errRate: s('Enter a VAT rate between 1 and 100.', 'أدخل نسبة ضريبة بين 1 و 100.'),
  },

  branch: {
    title: s('Set up your first branch', 'جهّز فرعك الأول'),
    lead: s(
      'This is where you sell. You can add more branches at any time.',
      'هذا هو مكان البيع. يمكنك إضافة فروع أخرى في أي وقت.',
    ),
    name: s('Branch name', 'اسم الفرع'),
    nameAr: s('Branch name in Arabic', 'اسم الفرع بالعربية'),
    city: s('City', 'المدينة'),
    street: s('Street', 'الشارع'),
    building: s('Building', 'المبنى'),
    phone: s('Contact number', 'رقم التواصل'),
    errName: s('Please give your branch a name.', 'يرجى إدخال اسم الفرع.'),
    moreLater: s(
      'More branches can be added later from the Sismatix ERP.',
      'يمكن إضافة المزيد من الفروع لاحقاً من نظام سيسماتكس ERP.',
    ),
  },

  features: {
    title: s('Choose your features', 'اختر المزايا'),
    lead: s(
      'We switched on what most businesses like yours use. Turn on anything else you need.',
      'فعّلنا ما تستخدمه أغلب الأنشطة المشابهة لنشاطك. فعّل أي ميزة أخرى تحتاجها.',
    ),
    modules: s('Modules', 'الوحدات'),
    orderTypes: s('Order types', 'أنواع الطلبات'),
    preferences: s('Preferences', 'التفضيلات'),
    tablesCount: s('How many tables do you have?', 'كم عدد الطاولات لديك؟'),
    tablesCountHint: s('We will create them for you. Enter 0 to add them later.', 'سننشئها لك. أدخل 0 لإضافتها لاحقاً.'),
    takeaway: s('Walk-in / Takeaway', 'سفري'),
    delivery: s('Delivery', 'توصيل'),
    pickup: s('Collection / Pickup', 'استلام من الفرع'),
    dineIn: s('Dine-in', 'محلي'),
    needsTables: s('Needs Tables', 'يتطلب الطاولات'),
    needsDelivery: s('Needs Delivery', 'يتطلب التوصيل'),
    productImages: s('Show product photos on the till', 'عرض صور المنتجات في نقطة البيع'),
    fullBarcode: s('Barcode scan matches the whole code', 'مطابقة الباركود كاملاً عند المسح'),
    combineQty: s('Scanning an item again adds to its quantity', 'إعادة مسح الصنف تزيد الكمية'),
    blockNegative: s('Stop selling when an item is out of stock', 'منع البيع عند نفاد المخزون'),
    staffOnLines: s('Assign a staff member to each service', 'تعيين موظف لكل خدمة'),
    included: s('Always included', 'متضمن دائماً'),
    includedList: s(
      'Sales, catalogue, customers, loyalty, promotions, coupons, gift cards and reports.',
      'المبيعات، الأصناف، العملاء، الولاء، العروض، الكوبونات، بطاقات الهدايا والتقارير.',
    ),
    oneOrderType: s('Choose at least one order type.', 'اختر نوع طلب واحداً على الأقل.'),
  },

  modules: {
    tables: s('Tables & floor plan', 'الطاولات والصالات'),
    tablesDesc: s('Seat guests, open orders on tables, split and merge bills.', 'استقبال الضيوف وفتح الطلبات على الطاولات وتقسيم ودمج الفواتير.'),
    kitchen: s('Kitchen & display', 'المطبخ وشاشة المطبخ'),
    kitchenDesc: s('Kitchen printers, kitchen display screens and printer routing.', 'طابعات المطبخ وشاشات عرض الطلبات وتوجيه الطباعة.'),
    delivery: s('Delivery', 'التوصيل'),
    deliveryDesc: s('Delivery orders, drivers and the delivery manager.', 'طلبات التوصيل والسائقين ومدير التوصيل.'),
    callCenter: s('Call center', 'مركز الاتصال'),
    callCenterDesc: s('Take phone orders for any branch.', 'استقبال الطلبات الهاتفية لأي فرع.'),
    externalOrders: s('Online ordering channels', 'قنوات الطلب الإلكتروني'),
    externalOrdersDesc: s('Receive orders from online ordering platforms.', 'استقبال الطلبات من منصات الطلب الإلكتروني.'),
    shipping: s('Shipping companies', 'شركات الشحن'),
    shippingDesc: s('Hand delivery orders to courier companies.', 'تسليم طلبات التوصيل لشركات الشحن.'),
    inventory: s('Inventory & warehouses', 'المخزون والمستودعات'),
    inventoryDesc: s('Stock levels, stock reports and transfers between stores.', 'أرصدة المخزون وتقاريره والتحويل بين المستودعات.'),
    appointments: s('Appointments & calendar', 'المواعيد والتقويم'),
    appointmentsDesc: s(
      'Book services, manage staff availability and the daily calendar.',
      'حجز الخدمات وإدارة أوقات الموظفين والتقويم اليومي.',
    ),
    onlineBooking: s('Online booking & deposits', 'الحجز الإلكتروني والعربون'),
    qrMenu: s('QR & online menu', 'قائمة QR والقائمة الإلكترونية'),
    qrView: s('QR menu (view only)', 'قائمة QR للعرض فقط'),
    qrViewDesc: s(
      'Guests scan a code to browse your menu in Arabic or English — no ordering.',
      'يمسح الضيوف رمزاً لتصفح قائمتك بالعربية أو الإنجليزية — بدون طلب.',
    ),
    qrMenuDesc: s(
      'Customers scan a code at the table to browse, order and rate your menu.',
      'يمسح العملاء رمزاً على الطاولة لتصفح القائمة والطلب والتقييم.',
    ),
    barcode: s('Barcode scanning', 'مسح الباركود'),
    barcodeDesc: s('Scan products and weighed-item labels at the till.', 'مسح المنتجات وملصقات الأصناف الموزونة في نقطة البيع.'),
    salesStaff: s('Sales staff & commissions', 'موظفو المبيعات والعمولات'),
    salesStaffDesc: s(
      'Name the salesperson or stylist on each item, for commissions and reports.',
      'تحديد موظف المبيعات أو المصفف لكل صنف، للعمولات والتقارير.',
    ),
    laundry: s('Laundry orders', 'طلبات المغسلة'),
    laundryDesc: s(
      'Order tracking with barcodes, ready / pickup / delivery statuses and pickup reminders.',
      'تتبّع الطلبات بالباركود وحالات الجاهزية والاستلام والتوصيل وتذكير الاستلام.',
    ),
    onlineBookingDesc: s(
      'Let customers book online and pay a deposit in advance.',
      'اسمح لعملائك بالحجز إلكترونياً ودفع عربون مقدماً.',
    ),
  },

  review: {
    title: s('Review your setup', 'راجع الإعداد'),
    lead: s('Check everything below. You can go back and change any step.', 'راجع كل شيء أدناه. يمكنك الرجوع وتعديل أي خطوة.'),
    businessType: s('Business type', 'نوع النشاط'),
    business: s('Business', 'النشاط'),
    branch: s('Branch', 'الفرع'),
    enabled: s('Enabled features', 'المزايا المفعّلة'),
    none: s('None', 'لا يوجد'),
    vatOff: s('Not registered for VAT', 'غير مسجّل في الضريبة'),
    vatOn: s('VAT {rate}%', 'ضريبة {rate}٪'),
    tables: s('{n} tables', '{n} طاولة'),
    core: s('Sales, catalogue, customers, loyalty and reports', 'المبيعات والأصناف والعملاء والولاء والتقارير'),
    finish: s('Finish setup', 'إنهاء الإعداد'),
    applying: s('Setting up your system…', 'جارٍ تجهيز نظامك…'),
  },

  done: {
    title: s("You're all set!", 'كل شيء جاهز!'),
    lead: s('Your Sismatix system is ready for your business.', 'نظام سيسماتكس جاهز لنشاطك التجاري.'),
    go: s('Go to Dashboard', 'الانتقال إلى لوحة التحكم'),
    warnings: s('A few things to finish in Settings:', 'بعض الأمور لإكمالها من الإعدادات:'),
  },

  warn: {
    CURRENCY_KEPT_TENANT_HAS_TRANSACTIONS: s(
      'Your currency was kept because transactions already exist.',
      'تم الإبقاء على العملة الحالية لوجود معاملات مسجّلة.',
    ),
    TABLES_KEPT_BRANCH_HAS_TABLES: s('Your branch already had tables, so none were added.', 'الفرع يحتوي على طاولات بالفعل، لذا لم تُضف طاولات جديدة.'),
    VAT_NOT_SAVED_NO_TAX_ROW: s('VAT could not be saved — set it in the ERP tax settings.', 'تعذّر حفظ الضريبة — اضبطها من إعدادات الضريبة في ERP.'),
    BRANCH_NOT_SAVED_NO_BRANCH: s('No branch was found to update.', 'لم يتم العثور على فرع لتحديثه.'),
    DRAWER_NOT_CREATED_LICENCE: s(
      'No cash drawer was added — your licence has no free point of sale. Contact Sismatix.',
      'لم تتم إضافة درج نقدية — لا توجد نقطة بيع متاحة في ترخيصك. تواصل مع سيسماتكس.',
    ),
    LOGO_NOT_SAVED_NO_BRANDING_TABLE: s('The logo could not be saved — upload it again in Branding.', 'تعذّر حفظ الشعار — ارفعه مرة أخرى من الهوية.'),
  } as Record<string, Bi>,

  reconfigure: {
    hi: s('Update your business setup', 'تحديث إعداد نشاطك التجاري'),
    lead: s('Change your business type, details or features.', 'غيّر نوع نشاطك أو بياناته أو مزاياه.'),
    body: s(
      'Your current answers are filled in. Nothing changes until you press Save on the last step — your sales, items and customers are never touched.',
      'إجاباتك الحالية معبأة مسبقاً. لا يتغير شيء حتى تضغط حفظ في الخطوة الأخيرة — ولا تتأثر مبيعاتك وأصنافك وعملاؤك أبداً.',
    ),
    start: s('Start', 'ابدأ'),
    cancel: s('Cancel', 'إلغاء'),
    warn: s(
      'Saving applies these settings now. Menus and screens that do not belong to the features you chose will be hidden for everyone in this business.',
      'عند الحفظ تُطبق هذه الإعدادات فوراً، وتُخفى القوائم والشاشات غير المرتبطة بالمزايا التي اخترتها لجميع مستخدمي هذا النشاط.',
    ),
    save: s('Save changes', 'حفظ التغييرات'),
    doneTitle: s('Your changes are saved', 'تم حفظ التغييرات'),
    doneLead: s('Your business setup has been updated.', 'تم تحديث إعداد نشاطك التجاري.'),
    back: s('Back to the dashboard', 'العودة إلى لوحة التحكم'),
  },

  owner: {
    title: s('Your business is being set up', 'جارٍ إعداد نشاطك التجاري'),
    lead: s(
      'Before anyone can sign in, the account owner needs to complete a short setup.',
      'قبل تسجيل الدخول، يجب على مالك الحساب إكمال إعداد قصير.',
    ),
    ask: s('If you are the owner, sign in with the administrator account to begin.', 'إذا كنت المالك، سجّل الدخول بحساب المدير للبدء.'),
    username: s('Administrator username', 'اسم مستخدم المدير'),
    password: s('Password', 'كلمة المرور'),
    signIn: s('Start setup', 'بدء الإعداد'),
    notOwner: s(
      'Not the owner? Ask your administrator to sign in and complete the setup.',
      'لست المالك؟ اطلب من المدير تسجيل الدخول وإكمال الإعداد.',
    ),
  },

  errors: {
    NETWORK: s('We could not reach the server. Check your connection and try again.', 'تعذّر الاتصال بالخادم. تحقق من الاتصال وحاول مجدداً.'),
    INVALID_TOKEN: s('Your setup session has expired. Please sign in again.', 'انتهت جلسة الإعداد. يرجى تسجيل الدخول مجدداً.'),
    SETUP_NOT_PENDING: s('Setup is already complete. You can sign in now.', 'تم الإعداد بالفعل. يمكنك تسجيل الدخول الآن.'),
    SETUP_OWNER_REQUIRED: s(
      'Those details are not an administrator account for this business.',
      'هذه البيانات ليست لحساب مدير لهذا النشاط.',
    ),
    INVALID_CURRENCY: s('That currency is not available.', 'هذه العملة غير متاحة.'),
    INVALID_REQUEST: s('Some details are not valid. Please review the steps.', 'بعض البيانات غير صحيحة. يرجى مراجعة الخطوات.'),
    DEFAULT: s('Something went wrong. Please try again.', 'حدث خطأ ما. يرجى المحاولة مجدداً.'),
  } as Record<string, Bi>,
};

export function fill(text: string, vars: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));
}
